import { useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { SummaryGraph } from '~widgets/summary-graph';
import type { SummaryDataField } from '~widgets/summary-graph';
import {
  SummaryTable,
  SummaryTableControls,
  useSummaryTable,
} from '~widgets/summary-table';

import { useIsDesktop } from '~shared/lib/responsive';
import { Container } from '~shared/ui/layout';
import { Loader } from '~shared/ui/loader';

import { Typography } from '@breezeventures/ui-kit/typography';

import { DashboardFilters } from './filters/dashboard-filters';
import { DashboardFiltersMobile } from './filters/dashboard-filters-mobile';
import { MetricChips } from './metric-chips';
import { DashboardPeriodFilter } from './period-filter';

import { filtersToParams } from '../lib';
import { useDashboardFilters } from '../model/use-dashboard-filters';

export const DashboardPage = () => {
  const {
    methods,
    appliedFilters,
    applyFilters,
    resetFilters,
    clearSelectFilters,
    activeFiltersCount,
    hideMerchantFilter,
  } = useDashboardFilters();
  const [dataField, setDataField] = useState<SummaryDataField>('loansAmount');
  const isDesktop = useIsDesktop();

  const params = useMemo(
    () => (appliedFilters ? filtersToParams(appliedFilters) : undefined),
    [appliedFilters]
  );

  const summaryTable = useSummaryTable(params);

  return (
    <FormProvider {...methods}>
      <Container>
        <div
          className={`
            box-border min-h-full w-full pt-6 pb-page-bottom-mobile
            lg:pt-10 lg:pb-16
          `}
        >
          <div
            className={`
              mb-4 flex w-full flex-wrap items-center justify-between gap-4
              lg:mb-6
            `}
          >
            <Typography.Title size="s" tag="h2">
              Dashboard
            </Typography.Title>
            <div className="flex flex-wrap items-center gap-3">
              {/*<TestCounterButton />*/}
              <MetricChips value={dataField} onChange={setDataField} />
              {isDesktop && <DashboardPeriodFilter onChange={applyFilters} />}
            </div>
          </div>

          <div className="flex flex-col gap-6">
            {isDesktop ? (
              <DashboardFilters
                hideMerchantFilter={hideMerchantFilter}
                onFiltersChange={applyFilters}
                onReset={resetFilters}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <DashboardPeriodFilter onChange={applyFilters} />
                {summaryTable.hasRows && (
                  <SummaryTableControls table={summaryTable} />
                )}
              </div>
            )}

            {params ? (
              <>
                <SummaryGraph filters={params} dataField={dataField} />
                <SummaryTable table={summaryTable} />
              </>
            ) : (
              <div className="relative flex min-h-[600px] flex-col">
                <Loader />
              </div>
            )}
          </div>

          {!isDesktop && (
            <DashboardFiltersMobile
              hideMerchantFilter={hideMerchantFilter}
              activeCount={activeFiltersCount}
              onApply={applyFilters}
              onClear={clearSelectFilters}
            />
          )}
        </div>
      </Container>
    </FormProvider>
  );
};
